import {
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  Timestamp,
  writeBatch,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { useEffect, useMemo, useState } from "react";
import { db, functions } from "../firebase";
import {
  assignMissingFamilyKeys,
  assignMissingParentKeys,
  buildStageRows,
  DEFAULT_PARSING_OPTIONS,
  normalizeEmail,
  parseSpreadsheetFile,
  splitName,
  suggestMapping,
  summarizeRows,
  revalidateStageRows,
  exampleForMappingField,
} from "./rollover/importHelpers";
import type { GuardianMapping, ImportMapping, ParsingOptions, StudentStage, GuardianStage, ImportedTable } from "./rollover/importHelpers";

type SchoolYearSettings = {
  activeYear: string;
  previousYear?: string;
  startDateIso?: string;
  endDateIso?: string;
  freezePreviousYearEdits?: boolean;
  retentionYears?: Record<string, number>;
  updatedAt?: Timestamp | null;
};

type StudentDoc = {
  studentId?: string;
  studentKey?: string;
  firstName?: string;
  lastName?: string;
  grade?: string;
  familyId?: string;
  isActive?: boolean;
};

type ParentDoc = {
  parentKey?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  familyId?: string;
  isActive?: boolean;
};

type FamilyDoc = {
  familyName?: string;
  isActive?: boolean;
  familyCodeUpper?: string;
};

type SequenceSettings = {
  nextParentNumber: number;
  nextFamilyNumber: number;
};

type RosterOptionItem = { value: string; label: string };
type RosterOptions = {
  gradeLevels: RosterOptionItem[];
  specialPrograms: RosterOptionItem[];
};

const DEFAULT_ROSTER_OPTIONS: RosterOptions = {
  gradeLevels: [
    { value: "PK", label: "Pre-K" },
    { value: "K", label: "Kindergarten" },
    { value: "1", label: "1st Grade" },
    { value: "2", label: "2nd Grade" },
    { value: "3", label: "3rd Grade" },
    { value: "4", label: "4th Grade" },
    { value: "5", label: "5th Grade" },
    { value: "6", label: "6th Grade" },
    { value: "7", label: "7th Grade" },
    { value: "8", label: "8th Grade" },
    { value: "9", label: "9th Grade" },
    { value: "10", label: "10th Grade" },
    { value: "11", label: "11th Grade" },
    { value: "12", label: "12th Grade" },
  ],
  specialPrograms: [{ value: "principal", label: "Principal" }],
};

type MappingTemplate = {
  id: string;
  name: string;
  mapping: ImportMapping;
  parsing?: ParsingOptions;
};

type ImportSession = {
  id: string;
  name: string;
  stageRows: StudentStage[];
  mapping: ImportMapping;
  parsing?: ParsingOptions;
  deactivateMissing: boolean;
  gradeAdvancementEnabled: boolean;
  excludePreK: boolean;
  excludeKindergarten: boolean;
  updatedAt?: any;
};

const DEFAULT_SEQUENCE: SequenceSettings = {
  nextParentNumber: 1000,
  nextFamilyNumber: 1000,
};

function todayIso() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function inferSpanLabelFromStart(startIso: string) {
  const y = Number(startIso.slice(0, 4));
  return `${y}-${y + 1}`;
}

function norm(v: any) {
  return String(v ?? "").trim();
}

function compareIsoDate(a: string, b: string): number {
  if (a === b) return 0;
  return a < b ? -1 : 1;
}

function dedupeStrings(values: string[]) {
  return Array.from(new Set(values.map((v) => norm(v)).filter(Boolean)));
}


function deriveNextSequenceFromRows(rows: StudentStage[], current: SequenceSettings): SequenceSettings {
  let nextParent = current.nextParentNumber;
  let nextFamily = current.nextFamilyNumber;

  rows.forEach((row) => {
    const familyMatch = /^F(\d+)$/i.exec(String(row.familyKey || "").trim());
    if (familyMatch) nextFamily = Math.max(nextFamily, Number(familyMatch[1]) + 1);
    row.guardians.forEach((g) => {
      const parentMatch = /^P(\d+)$/i.exec(String(g.parentKey || "").trim());
      if (parentMatch) nextParent = Math.max(nextParent, Number(parentMatch[1]) + 1);
    });
  });

  return { nextParentNumber: nextParent, nextFamilyNumber: nextFamily };
}

function readRosterItems(src: any, fallback: RosterOptionItem[]) {
  if (!Array.isArray(src)) return fallback;
  const out: RosterOptionItem[] = [];
  const seen = new Set<string>();
  for (const item of src) {
    const value = norm(typeof item === "string" ? item : item?.value);
    const label = norm(typeof item === "string" ? item : item?.label) || value;
    if (!value) continue;
    const key = value.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ value, label });
  }
  return out.length ? out : fallback;
}

function ensurePrincipalSpecialProgram(items: RosterOptionItem[]) {
  return items.some((item) => item.value.trim().toLowerCase() === "principal")
    ? items
    : [...items, { value: "principal", label: "Principal" }];
}

function guessNameFormatFromTable(table: ImportedTable | null, header: string): ParsingOptions["studentNameFormat"] {
  if (!table || !header) return "auto";
  let commaCount = 0;
  let seen = 0;
  for (const row of table.rows.slice(0, 20)) {
    const value = norm(row[header]);
    if (!value) continue;
    seen += 1;
    if (value.includes(',')) commaCount += 1;
  }
  if (seen > 0 && commaCount >= Math.max(2, Math.floor(seen / 2))) return "last_first";
  return "auto";
}

function gradeAdvanceValue(grade: string, excludePreK: boolean, excludeK: boolean) {
  const g = norm(grade).toUpperCase();
  if (!g) return grade;
  if (g === "PK") return excludePreK ? grade : "K";
  if (g === "K") return excludeK ? grade : "1";
  const n = Number(g);
  if (Number.isFinite(n) && n >= 1 && n <= 11) return String(n + 1);
  if (g === "12") return grade;
  return grade;
}

function fullName(v: { firstName?: string; lastName?: string }) {
  return `${v.firstName ?? ""} ${v.lastName ?? ""}`.trim();
}
function parseSequenceNumber(value: string, prefix: "F" | "P"): number | null {
  const m = new RegExp(`^${prefix}(\d+)$`, "i").exec(String(value || "").trim());
  return m ? Number(m[1]) : null;
}

function nextSequenceStartFromExisting(values: string[], configuredStart: number, prefix: "F" | "P"): number {
  let next = configuredStart;
  values.forEach((value) => {
    const parsed = parseSequenceNumber(value, prefix);
    if (parsed !== null) next = Math.max(next, parsed + 1);
  });
  return next;
}

function makeGuardianMappingSlot(): GuardianMapping {
  return { name: "", email: "", parentKey: "", relationship: "" };
}

function emptyMapping(): ImportMapping {
  return {
    studentId: "",
    studentKey: "",
    studentName: "",
    firstName: "",
    lastName: "",
    grade: "",
    householdName: "",
    familyKey: { mode: "", header: "" },
    familyName: "",
    isActive: "",
    specialsAssignments: "",
    guardians: [makeGuardianMappingSlot(), makeGuardianMappingSlot()],
  };
}

function linkId(studentKey: string, parentKey: string) {
  return `${studentKey}_${parentKey}`;
}

export function SchoolYearPage() {
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [resultMsg, setResultMsg] = useState<string | null>(null);
  const [running, setRunning] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);

  const [current, setCurrent] = useState<SchoolYearSettings | null>(null);
  const [startDateIso, setStartDateIso] = useState<string>(todayIso());
  const [endDateIso, setEndDateIso] = useState<string>(todayIso());
  const [newYearLabel, setNewYearLabel] = useState<string>("");
  const [freezePrevEdits, setFreezePrevEdits] = useState(true);

  const [sequenceSettings, setSequenceSettings] = useState<SequenceSettings>(DEFAULT_SEQUENCE);
  const [rosterOptions, setRosterOptions] = useState<RosterOptions>(DEFAULT_ROSTER_OPTIONS);

  const [existingStudents, setExistingStudents] = useState<Record<string, StudentDoc>>({});
  const [existingParentsByKey, setExistingParentsByKey] = useState<Record<string, ParentDoc>>({});
  const [existingParentsByEmail, setExistingParentsByEmail] = useState<Record<string, { id: string; data: ParentDoc }>>({});
  const [existingFamilies, setExistingFamilies] = useState<Record<string, FamilyDoc>>({});

  const [rawTable, setRawTable] = useState<ImportedTable | null>(null);
  const [rawHeaders, setRawHeaders] = useState<string[]>([]);
  const [rawRowsCount, setRawRowsCount] = useState(0);
  const [mapping, setMapping] = useState<ImportMapping>(emptyMapping());
  const [parsing, setParsing] = useState<ParsingOptions>(DEFAULT_PARSING_OPTIONS);
  const [sourceFileName, setSourceFileName] = useState<string>("");

  function mappingHeaderValue(field: keyof ImportMapping) {
    const value = (mapping as any)[field];
    if (field === "familyKey") return mapping.familyKey.header || "";
    return typeof value === "string" ? value : "";
  }

  function setMappingHeader(field: keyof ImportMapping, value: string) {
    if (field === "familyKey") {
      setMapping((prev) => ({ ...prev, familyKey: { mode: value ? "column" : "", header: value } }));
      return;
    }
    setMapping((prev) => ({ ...prev, [field]: value } as ImportMapping));
  }

  const assignParentKeysFromMapping = mapping.guardians.some((g) => g.parentKey === "__assign_next_parent__");
  const assignFamilyKeysFromMapping = mapping.familyKey.mode === "assign_next_family";
  function applyAutomaticKeyAssignments(rows: StudentStage[]) {
    return rows.map((r) => ({ ...r, guardians: r.guardians.map((g) => ({ ...g })) }));
  }

  function finalizeRowsForCommit(rows: StudentStage[]) {
    const nextRows = rows.map((row) => ({
      ...row,
      familyKey: mapping.familyKey.mode === "assign_next_family" ? "" : row.familyKey,
      guardians: row.guardians.map((guardian) => {
        const email = normalizeEmail(guardian.email);
        const existingKey = guardian.parentKey && existingParentsByKey[guardian.parentKey]
          ? guardian.parentKey
          : (email && existingParentsByEmail[email]?.data?.parentKey) || "";
        return {
          ...guardian,
          parentKey: guardian.parentKey === "__assign_next_parent__" ? (existingKey || "") : (guardian.parentKey || existingKey || ""),
        };
      }),
    }));

    return revalidateStageRows(nextRows);
  }


  const [stageRows, setStageRows] = useState<StudentStage[]>([]);
  const [deactivateMissing, setDeactivateMissing] = useState(false);
  const [gradeAdvancementEnabled, setGradeAdvancementEnabled] = useState(false);
  const [excludePreK, setExcludePreK] = useState(true);
  const [excludeKindergarten, setExcludeKindergarten] = useState(true);

  const [templateName, setTemplateName] = useState("");
  const [templates, setTemplates] = useState<MappingTemplate[]>([]);
  const [sessionName, setSessionName] = useState("");
  const [sessions, setSessions] = useState<ImportSession[]>([]);

  async function loadAll() {
    setLoading(true);
    setErr(null);
    try {
      const [
        schoolYearSnap,
        seqSnap,
        studentsSnap,
        parentsSnap,
        familiesSnap,
        templatesSnap,
        sessionsSnap,
        rosterOptionsSnap,
      ] = await Promise.all([
        getDoc(doc(db, "settings", "schoolYear")),
        getDoc(doc(db, "settings", "importSequences")),
        getDocs(collection(db, "students")),
        getDocs(collection(db, "parents")),
        getDocs(collection(db, "families")),
        getDocs(query(collection(db, "importTemplates"), orderBy("updatedAt", "desc"))),
        getDocs(query(collection(db, "importSessions"), orderBy("updatedAt", "desc"))),
        getDoc(doc(db, "settings", "rosterOptions")),
      ]);

      const schoolYear = schoolYearSnap.exists() ? (schoolYearSnap.data() as any) : null;
      setCurrent(schoolYear);
      const sd = schoolYear?.startDateIso ?? todayIso();
      setStartDateIso(sd);
      setEndDateIso(schoolYear?.endDateIso ?? todayIso());
      setNewYearLabel((prev) => prev || schoolYear?.activeYear || inferSpanLabelFromStart(sd));
      setFreezePrevEdits(schoolYear?.freezePreviousYearEdits ?? true);

      const seq = seqSnap.exists() ? (seqSnap.data() as any) : {};
      setSequenceSettings({
        nextParentNumber: Number.isFinite(Number(seq.nextParentNumber)) ? Number(seq.nextParentNumber) : DEFAULT_SEQUENCE.nextParentNumber,
        nextFamilyNumber: Number.isFinite(Number(seq.nextFamilyNumber)) ? Number(seq.nextFamilyNumber) : DEFAULT_SEQUENCE.nextFamilyNumber,
      });

      if (rosterOptionsSnap.exists()) {
        const d: any = rosterOptionsSnap.data();
        setRosterOptions({
          gradeLevels: readRosterItems(d?.gradeLevels, DEFAULT_ROSTER_OPTIONS.gradeLevels),
          specialPrograms: ensurePrincipalSpecialProgram(readRosterItems(d?.specialPrograms, DEFAULT_ROSTER_OPTIONS.specialPrograms)),
        });
      } else {
        setRosterOptions(DEFAULT_ROSTER_OPTIONS);
      }

      const studentMap: Record<string, StudentDoc> = {};
      studentsSnap.forEach((d) => {
        const data: any = d.data();
        const id = norm(data.studentId || data.studentKey || d.id);
        if (id) studentMap[id] = { ...data, studentKey: norm(data.studentKey || d.id), studentId: id };
      });
      setExistingStudents(studentMap);

      const parentKeyMap: Record<string, ParentDoc> = {};
      const parentEmailMap: Record<string, { id: string; data: ParentDoc }> = {};
      parentsSnap.forEach((d) => {
        const data: any = d.data();
        const key = norm(data.parentKey || d.id);
        const email = normalizeEmail(data.email || data.parentEmail);
        const row: ParentDoc = { ...data, parentKey: key, email };
        if (key) parentKeyMap[key] = row;
        if (email) parentEmailMap[email] = { id: d.id, data: row };
      });
      setExistingParentsByKey(parentKeyMap);
      setExistingParentsByEmail(parentEmailMap);

      const familyMap: Record<string, FamilyDoc> = {};
      familiesSnap.forEach((d) => {
        familyMap[d.id] = d.data() as any;
      });
      setExistingFamilies(familyMap);

      setTemplates(templatesSnap.docs.map((d) => ({ id: d.id, ...(d.data() as any) })));
      setSessions(sessionsSnap.docs.map((d) => ({ id: d.id, ...(d.data() as any) })));
    } catch (e: any) {
      setErr(e?.message || "Failed to load school year reset data.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadAll();
  }, []);

  const validation = useMemo(() => summarizeRows(stageRows), [stageRows]);


  const plannedRows = useMemo(() => {
    return stageRows.map((row) => ({
      ...row,
      grade: gradeAdvancementEnabled ? gradeAdvanceValue(row.grade, excludePreK, excludeKindergarten) : row.grade,
    }));
  }, [stageRows, gradeAdvancementEnabled, excludePreK, excludeKindergarten]);

  const rowsWithConflicts = useMemo(() => plannedRows.map((row) => {
    const conflicts: string[] = [];
    const key = norm(row.studentKey || row.studentId);
    if (key && existingStudents[key]) conflicts.push(`Existing student ${key} found in roster.`);
    if (row.familyKey && existingFamilies[row.familyKey]) conflicts.push(`Existing family ${row.familyKey} found.`);
    row.guardians.forEach((g) => {
      if (g.parentKey && existingParentsByKey[g.parentKey]) conflicts.push(`Existing parent ${g.parentKey} found.`);
      else if (g.email && existingParentsByEmail[g.email]) conflicts.push(`Existing parent email ${g.email} found.`);
    });
    return { ...row, duplicateSummary: conflicts };
  }), [plannedRows, existingStudents, existingFamilies, existingParentsByKey, existingParentsByEmail]);
  const gradeOptions = useMemo(() => rosterOptions.gradeLevels, [rosterOptions]);

  const missingActiveStudents = useMemo(() => {
    if (!deactivateMissing || !plannedRows.length) return [] as Array<{ id: string; data: StudentDoc }>;
    const importedIds = new Set(plannedRows.map((r) => norm(r.studentId || r.studentKey)).filter(Boolean));
    return Object.entries(existingStudents)
      .filter(([id, data]) => (data.isActive ?? true) && !importedIds.has(id))
      .map(([id, data]) => ({ id, data }));
  }, [deactivateMissing, plannedRows, existingStudents]);

  const commitSummary = useMemo(() => {
    const studentCreates: string[] = [];
    const studentUpdates: string[] = [];
    const parentCreates = new Set<string>();
    const parentUpdates = new Set<string>();
    const familyCreates = new Set<string>();
    const familyUpdates = new Set<string>();
    const linkCreates = new Set<string>();

    plannedRows.forEach((row) => {
      const studentId = norm(row.studentId || row.studentKey);
      if (!existingStudents[studentId]) studentCreates.push(studentId);
      else studentUpdates.push(studentId);

      if (row.familyKey) {
        if (existingFamilies[row.familyKey]) familyUpdates.add(row.familyKey);
        else familyCreates.add(row.familyKey);
      }

      row.guardians.forEach((g) => {
        if (!g.parentKey) return;
        if (existingParentsByKey[g.parentKey] || (g.email && existingParentsByEmail[g.email])) parentUpdates.add(g.parentKey);
        else parentCreates.add(g.parentKey);
        linkCreates.add(linkId(norm(row.studentKey || row.studentId), g.parentKey));
      });
    });

    return {
      studentCreates: studentCreates.length,
      studentUpdates: studentUpdates.length,
      parentCreates: parentCreates.size,
      parentUpdates: parentUpdates.size,
      familyCreates: familyCreates.size,
      familyUpdates: familyUpdates.size,
      linkCreates: linkCreates.size,
      studentsToDeactivate: missingActiveStudents.length,
      fatalErrors: plannedRows.filter((r) => r.errors.length > 0).length,
    };
  }, [plannedRows, existingStudents, existingParentsByKey, existingParentsByEmail, existingFamilies, missingActiveStudents]);

  async function onFilePick(file: File | null) {
    if (!file) return;
    setErr(null);
    setResultMsg(null);
    try {
      const parsed = await parseSpreadsheetFile(file);
      setRawTable(parsed);
      setRawHeaders(parsed.headers);
      setRawRowsCount(parsed.rows.length);
      setSourceFileName(file.name);
      const nextMapping = suggestMapping(parsed.headers);
      const nextParsing = { ...DEFAULT_PARSING_OPTIONS, studentNameFormat: guessNameFormatFromTable(parsed, nextMapping.studentName), guardianNameFormat: guessNameFormatFromTable(parsed, nextMapping.guardians[0]?.name || "") };
      setParsing(nextParsing);
      setMapping(nextMapping);
      setStageRows(revalidateStageRows(applyAutomaticKeyAssignments(buildStageRows(parsed, nextMapping, nextParsing))));
      setTemplateName(file.name.replace(/\.[^.]+$/, ""));
      setSessionName(file.name.replace(/\.[^.]+$/, ""));
    } catch (e: any) {
      setErr(e?.message || "Failed to parse file.");
    }
  }

  function rebuildStage() {
    if (!rawTable) {
      setResultMsg("Upload a file first.");
      return;
    }

    let nextRows = applyAutomaticKeyAssignments(buildStageRows(rawTable, mapping, parsing));

    setStageRows(revalidateStageRows(nextRows));
    setResultMsg("Mapping and parsing rules applied to the staging editor.");
  }

  async function saveTemplate() {
    const name = norm(templateName);
    if (!name) return alert("Template name is required.");
    await setDoc(doc(collection(db, "importTemplates")), {
      name,
      mapping,
      parsing,
      updatedAt: serverTimestamp(),
    });
    setResultMsg(`Saved mapping template "${name}".`);
    await loadAll();
  }

  async function saveSession() {
    const name = norm(sessionName);
    if (!name) return alert("Draft/session name is required.");
    await setDoc(doc(collection(db, "importSessions")), {
      name,
      stageRows,
      mapping,
      parsing,
      deactivateMissing,
      gradeAdvancementEnabled,
      excludePreK,
      excludeKindergarten,
      updatedAt: serverTimestamp(),
    });
    setResultMsg(`Saved draft session "${name}".`);
    await loadAll();
  }

  function loadTemplate(item: MappingTemplate) {
    setMapping(item.mapping);
    setParsing(item.parsing || DEFAULT_PARSING_OPTIONS);
    setTemplateName(item.name);
    setResultMsg(`Loaded template "${item.name}". Re-upload source file if you need to fully re-parse with it.`);
  }

  function loadSession(item: ImportSession) {
    setMapping(item.mapping);
    setStageRows(revalidateStageRows(item.stageRows || []));
    setParsing(item.parsing || DEFAULT_PARSING_OPTIONS);
    setDeactivateMissing(item.deactivateMissing === true);
    setGradeAdvancementEnabled(item.gradeAdvancementEnabled === true);
    setExcludePreK(item.excludePreK !== false);
    setExcludeKindergarten(item.excludeKindergarten !== false);
    setSessionName(item.name);
    setResultMsg(`Loaded draft session "${item.name}".`);
  }

  function updateStageRow(localId: string, patch: Partial<StudentStage>) {
    setStageRows((prev) => revalidateStageRows(prev.map((r) => (r.localId === localId ? { ...r, ...patch } : r))));
  }

  function removeStageRow(localId: string) {
    setStageRows((prev) => revalidateStageRows(prev.filter((r) => r.localId !== localId)));
  }

  function updateGuardian(studentLocalId: string, guardianLocalId: string, patch: Partial<GuardianStage>) {
    setStageRows((prev) =>
      revalidateStageRows(
        prev.map((r) =>
          r.localId === studentLocalId
            ? { ...r, guardians: r.guardians.map((g) => (g.localId === guardianLocalId ? { ...g, ...patch } : g)) }
            : r
        )
      )
    );
  }

  function addGuardian(studentLocalId: string) {
    setStageRows((prev) =>
      revalidateStageRows(
      prev.map((r) =>
        r.localId === studentLocalId
          ? {
              ...r,
              guardians: [
                ...r.guardians,
                { localId: `g-${Date.now()}-${Math.random()}`, name: "", firstName: "", lastName: "", email: "", relationship: "", parentKey: "" },
              ],
            }
          : r
      )
      )
    );
  }

  function removeGuardian(studentLocalId: string, guardianLocalId: string) {
    setStageRows((prev) =>
      revalidateStageRows(
        prev.map((r) =>
          r.localId === studentLocalId ? { ...r, guardians: r.guardians.filter((g) => g.localId !== guardianLocalId) } : r
        )
      )
    );
  }

  function applyAssignMissingFamilyKeys() {
    setStageRows((prev) => revalidateStageRows(prev.map((row) => ({ ...row, familyKey: row.familyKey ? row.familyKey : "" }))));
  }

  function applyAssignMissingParentKeys() {
    setStageRows((prev) => revalidateStageRows(prev.map((row) => ({
      ...row,
      guardians: row.guardians.map((guardian) => ({
        ...guardian,
        parentKey: guardian.parentKey && guardian.parentKey !== "__assign_next_parent__" ? guardian.parentKey : "",
      })),
    }))));
  }


  function buildParentProfileSyncPayload(rows: StudentStage[]) {
    const byEmail = new Map<string, { email: string; parentKey: string; familyId: string | null; isActive: boolean }>();
    rows.forEach((row) => {
      const familyId = norm(row.familyKey) || null;
      row.guardians.forEach((guardian) => {
        const email = normalizeEmail(guardian.email);
        const parentKey = norm(guardian.parentKey || guardian.existingParentId);
        if (!email || !parentKey) return;
        byEmail.set(email, {
          email,
          parentKey,
          familyId,
          isActive: row.isActive !== false,
        });
      });
    });
    return Array.from(byEmail.values());
  }

  function validateSchoolYearSettings() {
    const activeYear = newYearLabel.trim();
    if (!activeYear) throw new Error("School year label is required.");
    if (!startDateIso || !endDateIso) throw new Error("Start and end dates are required.");
    if (compareIsoDate(startDateIso, endDateIso) > 0) throw new Error("Start date must be on or before end date.");

    return {
      activeYear,
      previousYear: current?.activeYear?.trim() && current.activeYear.trim() !== activeYear
        ? current.activeYear.trim()
        : current?.previousYear?.trim() || null,
      startDateIso,
      endDateIso,
      freezePreviousYearEdits: freezePrevEdits,
    };
  }

  async function saveSchoolYearSettingsOnly() {
    setErr(null);
    setResultMsg(null);
    setSavingSettings(true);
    try {
      const commitFn = httpsCallable(functions, "commitRosterImport");
      await commitFn({
        rows: [],
        schoolYearUpdate: validateSchoolYearSettings(),
      });
      setResultMsg("School year settings saved.");
      await loadAll();
    } catch (e: any) {
      setErr(e?.message || "Failed to save school year settings.");
    } finally {
      setSavingSettings(false);
    }
  }

  async function commitAll() {
    setErr(null);
    setResultMsg(null);
    setRunning(true);
    try {
      const schoolYearUpdate = validateSchoolYearSettings();
      if (!plannedRows.length) throw new Error("Upload and review a Students + Guardians file before committing.");
      if (plannedRows.some((r) => r.errors.length > 0)) throw new Error("Fix all staging errors before commit.");
      const finalRows = finalizeRowsForCommit(plannedRows);
      if (finalRows.some((r) => r.errors.length > 0)) throw new Error("Fix all staging errors before commit.");
      const commitFn = httpsCallable(functions, "commitRosterImport");
      await commitFn({
        rows: finalRows,
        sourceFileName,
        deactivateMissing,
        schoolYearUpdate,
      });
      setResultMsg("School year rollover committed successfully.");
      await loadAll();
    } catch (e: any) {
      setErr(e?.message || "Failed to commit school year rollover.");
    } finally {
      setRunning(false);
    }
  }

  return (
    <div style={{ display: "grid", gap: 16 }}>
      <div className="card">
        <h2>School Year Reset</h2>
        <p className="muted">Unified rollover workflow for school year settings, grade advancement, and student/guardian import review.</p>
        {err ? <div className="error">{err}</div> : null}
        {resultMsg ? <div style={{ padding: 10, borderRadius: 10, background: "#ecfeff", color: "#155e75" }}>{resultMsg}</div> : null}
      </div>

      <div className="card">
        <h3>1. School Year Settings</h3>
        <div className="muted" style={{ marginBottom: 12 }}>Use this section by itself when you only need to set the active school year label and date range.</div>
        <div className="grid2">
          <label className="muted" style={{ display: "grid", gap: 6 }}>Active year label<input className="input" value={newYearLabel} onChange={(e) => setNewYearLabel(e.target.value)} placeholder="2026-2027" /></label>
          <div />
          <label className="muted" style={{ display: "grid", gap: 6 }}>Start date<input className="input" type="date" value={startDateIso} onChange={(e) => setStartDateIso(e.target.value)} /></label>
          <label className="muted" style={{ display: "grid", gap: 6 }}>End date<input className="input" type="date" value={endDateIso} onChange={(e) => setEndDateIso(e.target.value)} /></label>
        </div>
        <label style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 10 }}>
          <input type="checkbox" checked={freezePrevEdits} onChange={(e) => setFreezePrevEdits(e.target.checked)} />
          <span>Freeze previous year edits</span>
        </label>
        <div style={{ marginTop: 12 }}>
          <button className="btn" disabled={savingSettings || running} onClick={() => void saveSchoolYearSettingsOnly()}>
            {savingSettings ? "Saving..." : "Save School Year Settings"}
          </button>
        </div>
      </div>

      <div className="card">
        <h3>2. Grade Advancement</h3>
        <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <input type="checkbox" checked={gradeAdvancementEnabled} onChange={(e) => setGradeAdvancementEnabled(e.target.checked)} />
          <span>Advance all imported students one grade during commit</span>
        </label>
        <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginTop: 10 }}>
          <label style={{ display: "flex", gap: 8, alignItems: "center" }}><input type="checkbox" checked={excludePreK} onChange={(e) => setExcludePreK(e.target.checked)} />Exclude Pre-K</label>
          <label style={{ display: "flex", gap: 8, alignItems: "center" }}><input type="checkbox" checked={excludeKindergarten} onChange={(e) => setExcludeKindergarten(e.target.checked)} />Exclude Kindergarten</label>
        </div>
      </div>

      <div className="card">
        <h3>3. Students + Guardians Import</h3>
        <div className="muted">Upload CSV/XLSX, preview the incoming data, then map it into the ZCA structure.</div>
        <div style={{ marginTop: 12, display: "grid", gap: 12 }}>
          <input type="file" accept=".csv,.xlsx,.xls" onChange={(e) => void onFilePick(e.target.files?.[0] || null)} />
          {sourceFileName ? <div className="muted">Loaded file: <strong>{sourceFileName}</strong> ({rawRowsCount} row(s), {rawHeaders.length} column(s))</div> : null}
          {rawTable?.rows?.length ? (
            <div style={{ overflowX: "auto" }}>
              <table className="table" style={{ width: "100%" }}>
                <thead>
                  <tr>{rawHeaders.map((h) => <th key={h}>{h}</th>)}</tr>
                </thead>
                <tbody>
                  {rawTable.rows.slice(0, 5).map((row, idx) => (
                    <tr key={idx}>{rawHeaders.map((h) => <td key={h}>{row[h]}</td>)}</tr>
                  ))}
                </tbody>
              </table>
              <div className="muted" style={{ marginTop: 8 }}>Showing first 5 source rows for mapping reference.</div>
            </div>
          ) : null}
        </div>
      </div>

      <div className="card">
        <h3>4. Mapping + Parsing</h3>
        <div className="muted">These are the ZCA fields we require. For each one, choose the column heading in your file that contains that data.</div>
        <div className="grid2" style={{ marginTop: 12 }}>
          <label className="muted" style={{ display: "grid", gap: 6 }}>Student full name parsing
            <select className="input" value={parsing.studentNameFormat} onChange={(e) => setParsing((prev) => ({ ...prev, studentNameFormat: e.target.value as ParsingOptions["studentNameFormat"] }))}>
              <option value="auto">Auto-detect</option>
              <option value="first_last">First Last</option>
              <option value="last_first">Last, First / Last First</option>
            </select>
          </label>
          <label className="muted" style={{ display: "grid", gap: 6 }}>Parent / guardian full name parsing
            <select className="input" value={parsing.guardianNameFormat} onChange={(e) => setParsing((prev) => ({ ...prev, guardianNameFormat: e.target.value as ParsingOptions["guardianNameFormat"] }))}>
              <option value="auto">Auto-detect</option>
              <option value="first_last">First Last</option>
              <option value="last_first">Last, First / Last First</option>
            </select>
          </label>
        </div>
        <div style={{ marginTop: 12, display: "grid", gap: 16 }}>
          <div style={{ fontWeight: 800 }}>ZCA student + family fields</div>
          <div className="grid2">
            {([
              ["Student Key", "studentKey", true, "Required. This is the unique student identifier in ZCA."],
              ["Student Full Name", "studentName", false, "Use this if your file has the full student name in one column."],
              ["Student First Name", "firstName", false, "Optional if Student Full Name is mapped and parsing is enabled."],
              ["Student Last Name", "lastName", false, "Optional if Student Full Name is mapped and parsing is enabled."],
              ["Grade", "grade", true, "Required. Choose the column containing the student grade."],
              ["Family Name", "familyName", true, "Required. Map the file column that represents the household / family display name."],
              ["Active Status", "isActive", false, "Optional. Defaults to active when not provided."],
              ["Specials Assignments", "specialsAssignments", false, "Optional. Multiple values can be split from a single column."],
            ] as const).map(([label, field, required, help]) => (
              <div key={String(field)} style={{ display: "grid", gap: 6 }}>
                <label className="muted" style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                  <span style={{ fontWeight: 700 }}>{String(label)}</span>
                  {required ? <span style={{ background: "#dbeafe", color: "#1d4ed8", padding: "2px 8px", borderRadius: 999, fontSize: 12 }}>Required</span> : <span style={{ background: "#f3f4f6", color: "#4b5563", padding: "2px 8px", borderRadius: 999, fontSize: 12 }}>Optional</span>}
                </label>
                <div className="muted">{help}</div>
                <select className="input" value={mappingHeaderValue(field as keyof ImportMapping)} onChange={(e) => setMappingHeader(field as keyof ImportMapping, e.target.value)}>
                  <option value="">-- not mapped --</option>
                  {rawHeaders.map((h) => <option key={h} value={h}>{h}</option>)}
                </select>
                <div className="muted">Example: {exampleForMappingField(rawTable, mappingHeaderValue(field as keyof ImportMapping)) || "No sample yet"}</div>
              </div>
            ))}
          </div>
          <div style={{ border: "1px solid #dbeafe", background: "#eff6ff", color: "#1d4ed8", borderRadius: 12, padding: 12 }}><strong>Family IDs</strong> are assigned automatically on commit. The import no longer maps or edits Family Key values.</div>

          <div style={{ fontWeight: 800 }}>ZCA parent / guardian fields</div>
          {mapping.guardians.map((g, idx) => (
            <div key={idx} style={{ border: "2px solid #d1d5db", borderRadius: 14, padding: 12, display: "grid", gap: 10, background: "#fafafa" }}>
              <div style={{ fontWeight: 700 }}>Parent / Guardian slot {idx + 1}</div>
              <div className="grid2">
                {([
                  ["Parent / Guardian Name", "name", true, "Required if this guardian is present in the file."],
                  ["Parent / Guardian Email", "email", false, "Recommended for invites and account linking."],
                  ["Relationship", "relationship", false, "Optional. Defaults to Parent if not provided."],
                ] as const).map(([label, field, required, help]) => (
                  <div key={String(field)} style={{ display: "grid", gap: 6 }}>
                    <label className="muted" style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                      <span style={{ fontWeight: 700 }}>{String(label)}</span>
                      {required ? <span style={{ background: "#dbeafe", color: "#1d4ed8", padding: "2px 8px", borderRadius: 999, fontSize: 12 }}>Needed when used</span> : <span style={{ background: "#f3f4f6", color: "#4b5563", padding: "2px 8px", borderRadius: 999, fontSize: 12 }}>Optional</span>}
                    </label>
                    <div className="muted">{help}</div>
                    <select className="input" value={(g as any)[field]} onChange={(e) => setMapping((prev) => ({ ...prev, guardians: prev.guardians.map((slot, sIdx) => sIdx === idx ? { ...slot, [field]: e.target.value } : slot) }))}>
                      <option value="">-- not mapped --</option>
                      {rawHeaders.map((h) => <option key={h} value={h}>{h}</option>)}
                    </select>
                    <div className="muted">Example: {exampleForMappingField(rawTable, (g as any)[field]) || "No sample yet"}</div>
                  </div>
                ))}
                <div style={{ gridColumn: "1 / -1", border: "1px solid #dbeafe", background: "#eff6ff", color: "#1d4ed8", borderRadius: 12, padding: 12 }}>Parent IDs are assigned automatically on commit. The import no longer maps Parent Key values.</div>
              </div>
            </div>
          ))}

          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <button className="btn secondary" onClick={() => setMapping((prev) => ({ ...prev, guardians: [...prev.guardians, makeGuardianMappingSlot()] }))}>Add Guardian Slot</button>
            <button className="btn secondary" onClick={rebuildStage}>Apply Mapping + Parsing</button>
          </div>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
            <input className="input" style={{ maxWidth: 280 }} value={templateName} onChange={(e) => setTemplateName(e.target.value)} placeholder="Mapping template name" />
            <button className="btn secondary" onClick={() => void saveTemplate()}>Save Mapping Template</button>
            <select className="input" style={{ maxWidth: 280 }} defaultValue="" onChange={(e) => { const item = templates.find((t) => t.id === e.target.value); if (item) loadTemplate(item); }}>
              <option value="">Load saved template...</option>
              {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
        </div>
      </div>
      <div className="card">
        <h3>5. Drafts</h3>
        <div className="muted">Number pool starting values are controlled in Settings. Mapping choices above can pull from those pools automatically when needed.</div>
        <div className="grid2" style={{ marginTop: 12 }}>
          <div className="muted">Parent IDs are reserved server-side at commit time.</div>
          <div className="muted">Family IDs are reserved server-side at commit time.</div>
        </div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 10, alignItems: "center" }}>
          <input className="input" style={{ maxWidth: 280 }} value={sessionName} onChange={(e) => setSessionName(e.target.value)} placeholder="Draft/session name" />
          <button className="btn secondary" onClick={() => void saveSession()}>Save Draft</button>
          <select className="input" style={{ maxWidth: 280 }} defaultValue="" onChange={(e) => { const item = sessions.find((s) => s.id === e.target.value); if (item) loadSession(item); }}>
            <option value="">Load saved draft...</option>
            {sessions.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
      </div>

      <div className="card">
        <h3>6. Staging Editor</h3>
        <label style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 12 }}>
          <input type="checkbox" checked={deactivateMissing} onChange={(e) => setDeactivateMissing(e.target.checked)} />
          <span>Mark currently active students not present in this uploaded list as inactive, and cascade family/parent deactivation when the last active student in a family is removed.</span>
        </label>
        <div className="muted" style={{ marginBottom: 12 }}>Validation: {validation.errors} error(s), {validation.warnings} warning(s). Family and parent IDs are assigned automatically at commit.</div>
        <div style={{ display: "grid", gap: 14 }}>
          {rowsWithConflicts.map((row) => (
            <div key={row.localId} style={{ border: "1px solid #e5e7eb", borderRadius: 14, padding: 14, display: "grid", gap: 10 }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
                <div>
                  <div style={{ fontWeight: 800 }}>{fullName(row) || row.studentKey || "Student"}</div>
                  <div className="muted">Merged from {row.sourceRowCount} source row(s)</div>
                </div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                  {row.duplicateSummary?.length ? <span style={{ background: "#e0e7ff", color: "#4338ca", padding: "4px 8px", borderRadius: 999 }}>Conflict review</span> : null}
                  {row.errors.length === 0 ? <span style={{ background: "#dcfce7", color: "#166534", padding: "4px 8px", borderRadius: 999 }}>✓ Ready to import</span> : null}
                  {row.errors.map((x, idx) => <span key={`e-${idx}`} style={{ background: "#fee2e2", color: "#991b1b", padding: "4px 8px", borderRadius: 999 }}>{x}</span>)}
                  {row.warnings.map((x, idx) => <span key={`w-${idx}`} style={{ background: "#fef3c7", color: "#92400e", padding: "4px 8px", borderRadius: 999 }}>{x}</span>)}
                  <button className="btn secondary" onClick={() => removeStageRow(row.localId)}>Remove record</button>
                </div>
              </div>
              {row.duplicateSummary?.length ? <div style={{ border: "1px solid #c7d2fe", background: "#eef2ff", padding: 12, borderRadius: 12, display: "grid", gap: 8 }}><div style={{ fontWeight: 700 }}>Existing record conflicts</div><div className="muted">{row.duplicateSummary.join(" ")}</div><label className="muted" style={{ display: "grid", gap: 6 }}>How should this import handle the conflict?<select className="input" value={row.conflictMode} onChange={(e) => updateStageRow(row.localId, { conflictMode: e.target.value as any })}><option value="merge">Merge where possible</option><option value="keep_existing">Keep existing / skip incoming</option><option value="overwrite">Overwrite with incoming</option></select></label></div> : null}
              <div style={{ border: "2px solid #d1d5db", borderRadius: 14, padding: 14, display: "grid", gap: 12, background: "#fff" }}>
                <div className="grid2">
                  <label className="muted" style={{ display: "grid", gap: 6 }}><span style={{ fontWeight: 700 }}>Student Key</span><input className="input" value={row.studentKey} onChange={(e) => updateStageRow(row.localId, { studentKey: e.target.value, studentId: e.target.value })} /></label>
                  <label className="muted" style={{ display: "grid", gap: 6 }}><span style={{ fontWeight: 700 }}>Grade</span><select className="input" value={row.grade} onChange={(e) => updateStageRow(row.localId, { grade: e.target.value })}><option value="">-- select grade --</option>{gradeOptions.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}</select></label>
                  <label className="muted" style={{ display: "grid", gap: 6 }}><span style={{ fontWeight: 700 }}>Student First Name</span><input className="input" value={row.firstName} onChange={(e) => updateStageRow(row.localId, { firstName: e.target.value })} /></label>
                  <label className="muted" style={{ display: "grid", gap: 6 }}><span style={{ fontWeight: 700 }}>Student Last Name</span><input className="input" value={row.lastName} onChange={(e) => updateStageRow(row.localId, { lastName: e.target.value, familyName: row.familyName || e.target.value, householdName: row.householdName || row.familyName || e.target.value })} /></label>
                  <label className="muted" style={{ display: "grid", gap: 6 }}><span style={{ fontWeight: 700 }}>Family Name</span><input className="input" value={row.familyName} onChange={(e) => updateStageRow(row.localId, { familyName: e.target.value, householdName: e.target.value })} /></label>
                  <div className="muted" style={{ display: "grid", gap: 6 }}><span style={{ fontWeight: 700 }}>Family ID</span><div style={{ border: "1px solid #dbeafe", background: "#eff6ff", color: "#1d4ed8", borderRadius: 10, padding: "10px 12px" }}>{row.familyKey ? row.familyKey : "Will assign automatically on commit"}</div></div>
                </div>
                <div style={{ display: "grid", gap: 8 }}>
                  <div style={{ fontWeight: 700 }}>Specials assignments</div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 8 }}>
                    {rosterOptions.specialPrograms.map((program) => (
                      <label key={program.value} style={{ display: "flex", gap: 8, alignItems: "center", border: "1px solid #e5e7eb", borderRadius: 10, padding: 8 }}>
                        <input type="checkbox" checked={(row.specialAssignments || []).includes(program.value)} onChange={(e) => updateStageRow(row.localId, { specialAssignments: e.target.checked ? [...(row.specialAssignments || []), program.value] : (row.specialAssignments || []).filter((x) => x !== program.value) })} />
                        <span>{program.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
              <div style={{ display: "grid", gap: 10 }}>
                <div style={{ fontWeight: 800, paddingTop: 4, borderTop: "3px solid #111827" }}>Parent / Guardian records</div>
                {row.guardians.map((g) => (
                  <div key={g.localId} style={{ border: "2px solid #d1d5db", borderRadius: 12, padding: 12, display: "grid", gap: 8, background: "#fafafa" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "center" }}>
                      <div style={{ fontWeight: 700 }}>{g.name || fullName(g) || g.email || "Guardian"}</div>
                      <button className="btn secondary" onClick={() => removeGuardian(row.localId, g.localId)}>Remove</button>
                    </div>
                    {g.warning ? <div style={{ color: "#92400e" }}>{g.warning}</div> : null}
                    <div className="grid2">
                      <label className="muted" style={{ display: "grid", gap: 6 }}>Name<input className="input" value={g.name} onChange={(e) => { const parts = splitName(e.target.value); updateGuardian(row.localId, g.localId, { name: e.target.value, firstName: parts.firstName, lastName: parts.lastName }); }} /></label>
                      <label className="muted" style={{ display: "grid", gap: 6 }}>Email<input className="input" value={g.email} onChange={(e) => updateGuardian(row.localId, g.localId, { email: normalizeEmail(e.target.value) })} /></label>
                      <div className="muted" style={{ display: "grid", gap: 6 }}>Parent ID<div style={{ border: "1px solid #dbeafe", background: "#eff6ff", color: "#1d4ed8", borderRadius: 10, padding: "10px 12px" }}>{g.parentKey ? g.parentKey : "Will assign automatically on commit"}</div></div>
                      <label className="muted" style={{ display: "grid", gap: 6 }}>Relationship<select className="input" value={g.relationship || "Parent"} onChange={(e) => updateGuardian(row.localId, g.localId, { relationship: e.target.value })}><option value="Parent">Parent</option><option value="Guardian">Guardian</option><option value="Grandparent">Grandparent</option><option value="Stepparent">Stepparent</option><option value="Other">Other</option></select></label>
                    </div>
                  </div>
                ))}
                <div><button className="btn secondary" onClick={() => addGuardian(row.localId)}>Add Guardian</button></div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="card">
        <h3>7. Preview</h3>
        <div className="grid2">
          <div><strong>Students to create:</strong> {commitSummary.studentCreates}</div>
          <div><strong>Students to update:</strong> {commitSummary.studentUpdates}</div>
          <div><strong>Parents to create:</strong> {commitSummary.parentCreates}</div>
          <div><strong>Parents to update:</strong> {commitSummary.parentUpdates}</div>
          <div><strong>Families to create:</strong> {commitSummary.familyCreates}</div>
          <div><strong>Families to update:</strong> {commitSummary.familyUpdates}</div>
          <div><strong>Links to create/update:</strong> {commitSummary.linkCreates}</div>
          <div><strong>Students to deactivate:</strong> {commitSummary.studentsToDeactivate}</div>
        </div>
        {missingActiveStudents.length ? (
          <div style={{ marginTop: 12 }}>
            <div style={{ fontWeight: 700, marginBottom: 6 }}>Students that would be marked inactive</div>
            <div className="muted">{missingActiveStudents.map((m) => `${fullName(m.data)} (${m.id})`).join(", ")}</div>
          </div>
        ) : null}
      </div>

      <div className="card">
        <h3>8. Commit</h3>
        <div className="muted">Use this only when you are ready to commit the roster rollover/import. School year label and dates can be saved separately in Step 1.</div>
        <div style={{ marginTop: 12 }}>
          <button className="btn" disabled={running || savingSettings || commitSummary.fatalErrors > 0} onClick={() => void commitAll()}>{running ? "Running..." : "Commit Roster Rollover / Import"}</button>
          {commitSummary.fatalErrors > 0 ? <div className="muted" style={{ marginTop: 8 }}>Fix staging errors before commit.</div> : null}
        </div>
      </div>
    </div>
  );
}
